/**
 * useStableIds Hook
 *
 * Generates stable unique IDs for objects using WeakMap.
 * IDs persist across renders as long as the object reference stays the same.
 *
 * This allows consumers to use SortableList without requiring an `id` field
 * on their items - IDs are generated automatically based on object identity.
 */

import { useRef, useCallback } from 'react';

/**
 * Hook that provides stable ID generation for objects
 *
 * @returns Object with getStableId function
 *
 * @example
 * ```tsx
 * const { getStableId } = useStableIds();
 *
 * items.map(item => {
 *   const id = getStableId(item); // Same object = same ID
 *   return <Item key={id} />;
 * });
 * ```
 */
export function useStableIds() {
  // WeakMap allows garbage collection when objects are no longer referenced
  const idMapRef = useRef(new WeakMap<object, string>());

  /**
   * Get a stable ID for an object.
   * Prefers explicit `id` string property on the item (survives object recreation).
   * Falls back to WeakMap-based identity (same reference = same ID).
   */
  const getStableId = useCallback((item: object): string => {
    const idMap = idMapRef.current;

    // Prefer explicit 'id' property — stable across object recreations (e.g., l10n extraction)
    const explicitId = (item as Record<string, unknown>).id;
    if (typeof explicitId === 'string' && explicitId) {
      idMap.set(item, explicitId);
      return explicitId;
    }

    if (!idMap.has(item)) {
      idMap.set(item, crypto.randomUUID());
    }

    return idMap.get(item)!;
  }, []);

  return { getStableId };
}
